import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { Resource } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { getConfig } from '../config/env-config';
import resolveCollectorUrl from './collector-url';

// OTel JS is pinned to sdk 1.25.1 / experimental 0.52.1 because react-scripts 4
// bundles with webpack 4, which ignores package.json "exports": from 0.53 on, the
// OTLP exporters import subpaths such as '@opentelemetry/otlp-exporter-base/browser-http'
// that webpack 4 cannot resolve (and the 2.x line also ships ES2022 syntax it cannot
// parse). Revisit these pins if the app moves to react-scripts 5.
export default function initTracing() {
  const collectorUrl = getConfig('OTEL_COLLECTOR_URL');
  if (!collectorUrl) {
    console.warn('OTEL_COLLECTOR_URL not configured — tracing disabled');
    return;
  }

  const resource = new Resource({
    [ATTR_SERVICE_NAME]: getConfig('OTEL_SERVICE_NAME') || 'tsy-installer-agreement',
    [ATTR_SERVICE_VERSION]: getConfig('APP_VERSION') || '0.0.0',
  });

  const exporter = new OTLPTraceExporter({
    url: `${resolveCollectorUrl(collectorUrl)}/v1/traces`,
  });

  // sdk-trace-web 1.25 has no `spanProcessors` constructor option.
  const provider = new WebTracerProvider({ resource });
  provider.addSpanProcessor(new BatchSpanProcessor(exporter));

  provider.register({
    contextManager: new ZoneContextManager(),
  });

  // Exclude the OTLP export requests themselves from instrumentation. The browser
  // exporter posts over XHR, so without this every trace/log export would generate
  // its own span, which is exported and traced again — a self-feeding loop that
  // amplifies under load. Derived from the collector URL so it stays correct if the
  // endpoint changes.
  const ignoreCollector = new RegExp(collectorUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({ ignoreUrls: [ignoreCollector] }),
      // The form is submitted through emailjs-com, which uses XMLHttpRequest —
      // FetchInstrumentation alone would capture none of those calls. No
      // propagateTraceHeaderCorsUrls is set, so no traceparent header is added to the
      // cross-origin EmailJS request (it would trigger a CORS preflight it may reject).
      new XMLHttpRequestInstrumentation({ ignoreUrls: [ignoreCollector] }),
    ],
  });
}
